/**
 * Web (browser dev) implementation of PlatformApi.
 *
 * Used when running in Vite dev on a Mac, sec-tv-simulator, or any
 * non-Tizen browser. Backing stores:
 *   - storage:    Cache Storage API + sidecar Cache Storage for metadata
 *   - systemInfo: navigator.* + Performance Memory API where available
 *   - power:      Screen Wake Lock API (Chrome 84+, Tizen 6.5 has it too
 *                 but we go through tizen.power there)
 *   - network:    navigator.onLine + 'online' / 'offline' events
 *   - alarm:      plain setTimeout (does NOT survive page reload!)
 *
 * Anything Tizen-only (e.g. real disk capacity) returns -1 or empty
 * strings as documented in `types.ts`.
 */

import type {
  AlarmHandle,
  NetworkInfo,
  NetworkType,
  PlatformApi,
  PlatformAlarm,
  PlatformApplication,
  PlatformNetwork,
  PlatformPower,
  PlatformStorage,
  PlatformSystemInfo,
  PowerLock,
  StorageEntry,
  SystemInfo,
} from './types';

// ── Storage (Cache Storage API) ─────────────────────────────────────

const BYTES_CACHE = 'sensar-storage-v1';
const META_CACHE = 'sensar-storage-meta-v1';

interface PersistedMeta {
  key: string;
  size: number;
  contentType: string | null;
  storedAt: number;
}

function storageKey(key: string): string {
  return `/${encodeURIComponent(key)}`;
}

class WebStorage implements PlatformStorage {
  readonly persistent = true;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    if (typeof caches === 'undefined') {
      console.warn('[platform/web] Cache Storage unavailable — storage is volatile');
      return;
    }
    // Touch both caches so subsequent gets/puts never hit "cache not found"
    await Promise.all([caches.open(BYTES_CACHE), caches.open(META_CACHE)]);

    // Best-effort request for persistent storage so the browser doesn't
    // evict our cache under quota pressure.
    if (navigator.storage?.persist) {
      try {
        const granted = await navigator.storage.persist();
        console.log(`[platform/web] persistent storage granted: ${granted}`);
      } catch {
        // Some browsers reject without a user gesture — fine on dev.
      }
    }
  }

  async get(key: string): Promise<Blob | null> {
    if (typeof caches === 'undefined') return null;
    const cache = await caches.open(BYTES_CACHE);
    const res = await cache.match(storageKey(key));
    return res ? await res.blob() : null;
  }

  async put(key: string, body: Blob, contentType?: string): Promise<StorageEntry> {
    if (typeof caches === 'undefined') {
      throw new Error('Cache Storage unavailable');
    }
    const ct = contentType ?? body.type ?? 'application/octet-stream';
    const meta: PersistedMeta = {
      key,
      size: body.size,
      contentType: ct,
      storedAt: Date.now(),
    };

    // Write meta first so a mid-write crash leaves us with orphaned meta
    // (we clean those at next init by checking bytes presence).
    const metaCache = await caches.open(META_CACHE);
    await metaCache.put(
      storageKey(key),
      new Response(JSON.stringify(meta), {
        headers: { 'Content-Type': 'application/json' },
      }),
    );

    const bytesCache = await caches.open(BYTES_CACHE);
    await bytesCache.put(
      storageKey(key),
      new Response(body, { headers: { 'Content-Type': ct } }),
    );

    return { key, size: meta.size, contentType: meta.contentType, storedAt: meta.storedAt };
  }

  async remove(key: string): Promise<void> {
    if (typeof caches === 'undefined') return;
    const [bytesCache, metaCache] = await Promise.all([
      caches.open(BYTES_CACHE),
      caches.open(META_CACHE),
    ]);
    await Promise.all([
      bytesCache.delete(storageKey(key)),
      metaCache.delete(storageKey(key)),
    ]);
  }

  async list(): Promise<StorageEntry[]> {
    if (typeof caches === 'undefined') return [];
    const metaCache = await caches.open(META_CACHE);
    const keys = await metaCache.keys();
    const out: StorageEntry[] = [];
    for (const req of keys) {
      const res = await metaCache.match(req);
      if (!res) continue;
      try {
        const m = (await res.json()) as PersistedMeta;
        out.push({
          key: m.key,
          size: m.size,
          contentType: m.contentType,
          storedAt: m.storedAt,
        });
      } catch {
        // Skip corrupt entries silently — they'll be cleaned on next init pass.
      }
    }
    return out;
  }

  async getQuota(): Promise<{ usage: number; quota: number }> {
    if (!navigator.storage?.estimate) return { usage: -1, quota: -1 };
    try {
      const est = await navigator.storage.estimate();
      return { usage: est.usage ?? -1, quota: est.quota ?? -1 };
    } catch {
      return { usage: -1, quota: -1 };
    }
  }
}

// ── System info (browser approximation) ─────────────────────────────

const APP_START = Date.now();

class WebSystemInfo implements PlatformSystemInfo {
  readonly available = true; // partial — some fields are -1

  async collect(): Promise<SystemInfo> {
    // navigator.storage.estimate gives disk-budget snapshot
    let diskFreeBytes = -1;
    let diskUsedPercent = -1;
    if (navigator.storage?.estimate) {
      try {
        const est = await navigator.storage.estimate();
        if (est.usage != null && est.quota != null && est.quota > 0) {
          diskUsedPercent = Math.round((est.usage / est.quota) * 100);
          diskFreeBytes = est.quota - est.usage;
        }
      } catch {
        // ignore
      }
    }

    // performance.memory is non-standard (Chrome only) but harmless to read.
    let ramUsedBytes = -1;
    const perfMem = (
      performance as { memory?: { usedJSHeapSize?: number } }
    ).memory;
    if (perfMem?.usedJSHeapSize) ramUsedBytes = perfMem.usedJSHeapSize;

    const appUptimeHours = (Date.now() - APP_START) / 3_600_000;

    let displayResolution: { width: number; height: number } | null = null;
    if (window.screen) {
      displayResolution = {
        width: window.screen.width,
        height: window.screen.height,
      };
    }

    return {
      diskUsedPercent,
      diskFreeBytes,
      ramUsedBytes,
      cpuLoad: -1, // not exposed in browser
      appUptimeHours,
      firmwareVersion: navigator.userAgent,
      deviceModel: navigator.platform || '',
      displayResolution,
    };
  }
}

// ── Power (Screen Wake Lock API) ────────────────────────────────────

class WebPower implements PlatformPower {
  readonly available = 'wakeLock' in navigator;

  async keepScreenOn(): Promise<PowerLock> {
    if (!this.available) {
      // Return a no-op handle — caller doesn't have to branch on
      // availability in the common case.
      return { release: () => {} };
    }
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
    };
    if (!nav.wakeLock) return { release: () => {} };
    try {
      const sentinel = await nav.wakeLock.request('screen');
      return {
        release: () => {
          void sentinel.release();
        },
      };
    } catch {
      return { release: () => {} };
    }
  }
}

// ── Network ─────────────────────────────────────────────────────────

function detectNetworkType(): NetworkType {
  if (!navigator.onLine) return 'offline';
  // navigator.connection (NetworkInformation API) — partial Chrome
  const conn = (
    navigator as Navigator & {
      connection?: { type?: string; effectiveType?: string };
    }
  ).connection;
  if (conn?.type === 'wifi') return 'wifi';
  if (conn?.type === 'ethernet') return 'ethernet';
  if (conn?.type === 'cellular') return 'cellular';
  return 'unknown';
}

class WebNetwork implements PlatformNetwork {
  readonly available = true;

  async info(): Promise<NetworkInfo> {
    return {
      type: detectNetworkType(),
      online: navigator.onLine,
    };
  }

  onChange(cb: (info: NetworkInfo) => void): () => void {
    const emit = () => {
      void this.info().then(cb);
    };
    window.addEventListener('online', emit);
    window.addEventListener('offline', emit);
    return () => {
      window.removeEventListener('online', emit);
      window.removeEventListener('offline', emit);
    };
  }
}

// ── Application ─────────────────────────────────────────────────────

class WebApplication implements PlatformApplication {
  readonly available = true;

  async info() {
    // Stub values — real ones come from Tizen on device.
    return {
      appId: 'web.dev',
      version: '0.0.0',
      packageId: 'web',
    };
  }

  exit(): void {
    // Browser equivalent of "kill + restart" is reload.
    window.location.reload();
  }
}

// ── Alarm (setTimeout-based; no background wake) ────────────────────

class WebAlarm implements PlatformAlarm {
  readonly available = true; // works, just doesn't survive reload

  async scheduleRelative(
    delaySec: number,
    callback: () => void,
  ): Promise<AlarmHandle> {
    const id = `web-alarm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timer = setTimeout(callback, delaySec * 1000);
    return {
      id,
      cancel: () => clearTimeout(timer),
    };
  }
}

// ── Public api ──────────────────────────────────────────────────────

export const webPlatform: PlatformApi = {
  kind: 'web',
  storage: new WebStorage(),
  systemInfo: new WebSystemInfo(),
  power: new WebPower(),
  network: new WebNetwork(),
  application: new WebApplication(),
  alarm: new WebAlarm(),
};
